// webapp/src/lib/offlineStorage.js
//
// Shared IndexedDB plumbing for the keepspot offline subsystem (Phase 1 read
// cache + Phase 2 sale write queue). All IDB lifecycle (open, upgrade,
// transactions) lives here. Higher-level modules (offlineCache,
// offlineWriteQueue) import getDb / tx / createKeyValueStore.

export const DB_NAME = 'keepspot-offline-cache';
export const DB_VERSION = 5;
export const STORE_API_RESPONSES = 'apiResponses';
export const STORE_PENDING_SALES = 'pendingSales';
export const STORE_PENDING_SCORES = 'pendingScores';
export const STORE_PENDING_IMAGES = 'pendingImages';
export const STORE_PENDING_IPSC_OPS = 'pendingIpscOps';

function openDb() {
    return new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('IndexedDB unavailable'));
            return;
        }
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_API_RESPONSES)) {
                // Phase 1 store: out-of-line key (we pass key on put).
                db.createObjectStore(STORE_API_RESPONSES);
            }
            if (!db.objectStoreNames.contains(STORE_PENDING_SALES)) {
                // Phase 2 store: keyPath 'tempId' (in-line negative-number key).
                db.createObjectStore(STORE_PENDING_SALES, { keyPath: 'tempId' });
            }
            if (!db.objectStoreNames.contains(STORE_PENDING_SCORES)) {
                // Phase 3 store: one entry per scored cell, keyPath 'key'
                // (string `${convention}-${participant}-${stage}-${stand}`).
                db.createObjectStore(STORE_PENDING_SCORES, { keyPath: 'key' });
            }
            if (!db.objectStoreNames.contains(STORE_PENDING_IMAGES)) {
                // Offline post creation: raw image Blobs waiting for upload,
                // keyPath 'key' (string `img-<ts>-<counter>`). Values are
                // structured-cloned — never JSON round-tripped.
                db.createObjectStore(STORE_PENDING_IMAGES, { keyPath: 'key' });
            }
            if (!db.objectStoreNames.contains(STORE_PENDING_IPSC_OPS)) {
                // IPSC scoresheet op log (lib/ipscScoreQueue.js): one entry per
                // operation, keyPath 'opKey' (string `${sheetKey}#${seq}`).
                db.createObjectStore(STORE_PENDING_IPSC_OPS, { keyPath: 'opKey' });
            }
        };
        req.onsuccess = () => {
            const db = req.result;
            db.onversionchange = () => {
                db.close();
                _dbPromise = null;
            };
            resolve(db);
        };
        req.onerror = () => reject(req.error);
        req.onblocked = () => reject(new Error('IndexedDB open blocked'));
    });
}

let _dbPromise = null;
export function getDb() {
    if (!_dbPromise) {
        _dbPromise = openDb().catch(err => {
            _dbPromise = null;
            throw err;
        });
    }
    return _dbPromise;
}

/**
 * Run a transaction against `storeName`. `fn(store)` may return either an
 * IDBRequest (whose result is used as the resolved value) or nothing.
 * Resolves with the IDBRequest result once the transaction completes.
 */
export function tx(mode, storeName, fn) {
    return getDb().then(db => new Promise((resolve, reject) => {
        const t = db.transaction(storeName, mode);
        const store = t.objectStore(storeName);
        let result;
        try {
            const req = fn(store);
            if (req && 'onsuccess' in req) {
                req.onsuccess = () => { result = req.result; };
                req.onerror = () => reject(req.error);
            }
        } catch (e) {
            reject(e);
            return;
        }
        t.oncomplete = () => resolve(result);
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error || new Error('transaction aborted'));
    }));
}

/**
 * Convenience wrapper for an out-of-line keyed store (key is passed at
 * put-time). Used by Phase 1's `apiResponses` store.
 */
export function createKeyValueStore(storeName) {
    return {
        get: (key) => tx('readonly', storeName, store => store.get(key)),
        put: (key, value) => tx('readwrite', storeName, store => store.put(value, key)),
        delete: (key) => tx('readwrite', storeName, store => store.delete(key)),
        deleteWhere: (predicate) => tx('readwrite', storeName, store => {
            const req = store.openCursor();
            req.onsuccess = (event) => {
                const cursor = event.target.result;
                if (!cursor) return;
                if (predicate(cursor.value, cursor.key)) {
                    cursor.delete();
                }
                cursor.continue();
            };
            // Don't return req — tx() rebinds onsuccess on whatever we return,
            // which would clobber our cursor handler and the cursor would never
            // iterate. Letting tx() resolve on transaction.oncomplete is enough.
        }),
        getAllMatching: (predicate) => {
            const matches = [];
            return tx('readonly', storeName, store => {
                const req = store.openCursor();
                req.onsuccess = (event) => {
                    const cursor = event.target.result;
                    if (!cursor) return;
                    if (predicate(cursor.value, cursor.key)) {
                        matches.push({ key: cursor.key, value: cursor.value });
                    }
                    cursor.continue();
                };
                // Same reason as deleteWhere — don't return req.
            }).then(() => matches);
        },
    };
}

// Test-only hook: drop the cached promise so a fresh DB instance is opened
// next time. Production code never calls this.
export function _resetForTests() {
    _dbPromise = null;
}
